export default {
	title: "",
	subtitle: "Subscription with BRL Automatic PIX",
  path: "subscription_brl_automatic_pix",
  paragraphs: [
    'BRL Automatic PIX subscriptions allow you to charge customers automatically through direct bank debits, providing a seamless recurring payment experience.',
    'To create an automatic PIX subscription, send a POST request with the required payload. The system will return a subscription in "pending" status and generate a dynamic QR Code asynchronously for customer authorization.',
    'Once generated, the QR Code will be included in the from_pix field of the subscription response, containing all payment details required for authorization. A webhook notification will also be sent to your endpoint to confirm that the QR Code is ready.',
    'The customer must scan this QR Code using their bank’s mobile app to authorize the automatic debit. This one-time authorization is mandatory — the subscription will only become active after the customer successfully completes the payment from their account.',
    'After activation, charges will occur automatically based on the defined frequency, amount, and cycle limits. Customers can view upcoming debit transactions in their banking app up to two days before each settlement date.',
    'If a payment fails due to insufficient funds, the system will retry according to your configured retry attempts. Each retry is scheduled every other day: for example, if a charge fails on the settlement date, the next debit attempt will be scheduled two days later. If that attempt also fails, another will be scheduled two days after, and so on, until the maximum number of retry attempts is reached. Once all retries are exhausted, the subscription will be canceled automatically, and a webhook notification will be sent to you.'
  ],
  request: {
    method: "POST",
		endpoint: "/subscriptions",
		payloads: [
      {
        "title": "Create a Subscription using automatic pix",
        "content": {
          "subscription": {
            "cycles": 12,
            "frequency": "month",
            "value": "100",
            "currency": "BRL",
            "description": "some description",
            "subject_id": "your subject_id",
            "to_bank_account_id": "your internal bank account id",
            "from_bank_account_id": "your exclusive BRL bank account id",
          }
        }
      }
    ],
		responses: {
      "data": {
        "canceled_at": null,
        "canceled_by_payer": null,
        "canceled_reason": null,
        "completed_at": null,
        "currency": "BRL",
        "cycles": 12,
        "description": "some description",
        "free_days": 0,
        "frequency": "month",
        "from_bank_account_id": "f29b519d-0b54-42b7-af09-6ad78f0a889a",
        "from_pix": {
          "qr_code": "00020126580014br.gov.bcb.pix0136123e4567-e89b-12d3-a456-426614174000520400005303986540510.005802BR5913FACILITA PAY6008BRASILIA62070503***63041D3D"
        },
        "id": "899758d4-145a-4481-9791-d28b254373cc",
        "inserted_at": "2025-09-01T15:47:20.803645",
        "payer_bank_account_id": null,
        "payer_card_id": null,
        "payment_method": "pix",
        "status": "pending",
        "subject_id": "b009558e-16b0-45dd-9bb7-7edea3d7a5cf",
        "to_bank_account_id": "f29b519d-0b54-42b7-af09-6ad78f0a889a",
        "total_retry_attempts": 0,
        "trial_days": 0,
        "updated_at": "2025-09-01T15:47:20.874607",
        "value": "100"
      }
    }
	},
	parameterPayload: [
    {
      name: "subscription",
      type: "object",
      required: true,
      description: "Subscription data containing at least the following required values:"
    },
    {
      name: "subscription.value",
      type: "string",
      required: true,
      description: 'Total amount to be charged. Only numbers with exponent size 2 are allowed. (e.g.: "12.34").'
    },
    {
      name: "subscription.currency",
      type: "string",
      required: true,
      description: "Defines the subscription currency in ISO 4217 format, currently the only accepted value is “BRL”."
    },
    {
      name: "subscription.frequency",
      type: "string",
      required: true,
      description: 'Defines how often the subscription will be charged. Possible values are: "week", "month", "quarter", "semester", "year". Note that "day" is not allowed for this type of subscription.'
    },
    {
      name: "subscription.cycles",
      type: "object",
      required: true,
      description: "Number of cycles the subscription will have. For example, if the frequency is “month” and the cycles is 12, the subscription will last for 12 months."
    },
    {
      name: "subscription.trial_days",
      type: "object",
      required: false,
      description: "Defines a trial period before the first charge. After the trial period ends, future charges always respect the original subscription date."
    },
    {
      name: "subscription.free_days",
      type: "object",
      required: false,
      description: "Defines a grace period before the first charge. After the grace period ends, future charges will occur on the date the first charge occurred."
    },
    {
      name: "subscription.total_retry_attempts",
      type: "object",
      required: false,
      description: "Defines how many retry attempts will be made if charges fail, except for the first invoice. The default value is zero and it can range from zero to three."
    },
    {
      name: "subscription.description",
      type: "object",
      required: false,
      description: "A brief description of the subscription."
    },
    {
      name: "subscription.to_bank_account_id",
      type: "string",
      required: true,
      description: "Partner internal or external international bank account ID"
    },
    {
      name: "subscription.from_bank_account_id",
      type: "string",
      required: true,
      description: "Your exclusive BRL bank account ID"
    },
    {
      name: "subscription.subject_id",
      type: "string",
      required: true,
      description: "The ID of the person or company involved in this subscription."
    },
    {
      name: "subscription.meta",
      type: "object",
      required: false,
      description: "Any metadata you want to add to the subscription to help you identify it later."
    },
  ],
  troubleshooting: {
    title: 'Common Issues',
    messages: [
       {
        description: 'You did not provide a valid from_bank_account_id',
        code: {
              "errors": {
                  "from_bank_account_id": [
                      "is invalid"
                  ]
              }
          },
        solution: 'You must provide the ID of your exclusive BRL bank account that we sent to you when we created your account. You can find it in the "Bank Accounts" page by logging in our portal and going to the "FacilitaPay pay-in accounts" section.'
      },
      {
        description: 'Subscription remains in "pending" status for too long',
        code: {
              "subscription": {
                  "status": "pending",
                  "from_pix": {
                      "qr_code": "qr_code_string"
                  }
              }
          },
        solution: 'Check if the customer has paid the QR Code. The "pending" status indicates that the QR Code was generated but not yet paid. The customer must scan the QR Code with their bank app and make the payment to activate the subscription.'
      },
      {
        description: 'Error generating dynamic QR Code',
        code: {
              "errors": {
                  "qr_code_generation": [
                      "Service unavailable"
                  ]
              }
          },
        solution: 'This error indicates a temporary issue with the QR Code generation service. Try again in a few minutes. If the problem persists, contact support.'
      },
      {
        description: 'Subscription automatically canceled',
        code: {
              "subscription": {
                  "status": "canceled",
                  "canceled_reason": "insufficient_funds"
              }
          },
        solution: 'The subscription was canceled because the customer did not have sufficient funds on the settlement day and the maximum number of retry attempts was reached. You can create a new subscription or contact the customer to resolve payment issues.'
      },
      {
        description: 'Webhook not received',
        code: null,
        solution: 'Verify that your webhook endpoint is properly configured and publicly accessible. Make sure it returns HTTP 200 status for notifications. Test your endpoint with tools like ngrok for local development.'
      }
    ]
  }
}



// WEBPACK FOOTER //
// ./src/components/documentation/data/Subscription/SubscriptionBRLAutomaticPix.js